import { FC } from 'react';

import { isNullish } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { SpecialDropFormValues } from '../form';
import { SpecialDropForm } from '../specialDropForm/SpecialDropForm';
import { getMultipliedRewardsPayload, getRewardsWithCount } from '../utils';

import { rewardsService } from 'v2/api/rewardsService';
import { specialDropService } from 'v2/api/specialDropService';
import { Loader } from 'v2/components/loader/Loader';
import { SpecialDropModel } from 'v2/types/SpecialDropModel';
import { handleHttpError } from 'v2/utils';

interface SpecialDropEditModalProps {
  isOpened: boolean;
  handleClose: VoidFunction;
  onRevalidate: VoidFunction;
  specialDrop: SpecialDropModel | null;
}

export const SpecialDropEditModal: FC<SpecialDropEditModalProps> = ({
  isOpened,
  handleClose,
  onRevalidate,
  specialDrop,
}) => {
  const { data: initialRewards = [], isLoading: isInitialRewardsLoading } = useSWR(
    isOpened && specialDrop ? ['getRewardsByIds', specialDrop.rewardIds] : null,
    rewardsService.getRewardsByIds
  );

  const { trigger: triggerDeleteRewards, isMutating: isDeletingRewards } = useSWRMutation(
    'deleteRewards',
    (_key, { arg }: { arg: string[] }) => rewardsService.deleteRewards(['deleteRewards', arg])
  );

  const { trigger: triggerCreateRewards, isMutating: isCreatingRewards } = useSWRMutation(
    'createRewards',
    rewardsService.createRewards
  );

  const { trigger: triggerUpdateSpecialDrop, isMutating: isUpdatingSpecialDrop } = useSWRMutation(
    specialDrop ? ['updateSpecialDrop', specialDrop.id] : null,
    specialDropService.updateSpecialDrop
  );

  const handleUpdateSpecialDrop = async (formValues: SpecialDropFormValues) => {
    if (isNullish(specialDrop)) {
      return;
    }

    try {
      if (specialDrop.rewardIds.length > 0) {
        await triggerDeleteRewards(specialDrop.rewardIds);
      }

      const rewardsPayload = getMultipliedRewardsPayload(formValues.rewards);
      const createdRewardIds = await triggerCreateRewards(rewardsPayload);

      await triggerUpdateSpecialDrop({
        chanceToWin: formValues.chanceToWin,
        type: formValues.type,
        rewardIds: createdRewardIds,
      });

      toast.success('Special drop has been successfully updated');
      onRevalidate();
      handleClose();
    } catch (error) {
      await handleHttpError(error);
    }
  };

  if (isNullish(specialDrop)) {
    return null;
  }

  const initialValues: SpecialDropFormValues = {
    chanceToWin: specialDrop.chanceToWin,
    type: specialDrop.type,
    rewards: getRewardsWithCount(initialRewards),
  };

  const isProcessing = isDeletingRewards || isCreatingRewards || isUpdatingSpecialDrop;

  return (
    <Dialog open={isOpened} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
      <DialogTitle>Edit Special Drop</DialogTitle>

      <DialogContent sx={{ '&&': { pt: 2 } }}>
        {isInitialRewardsLoading ? (
          <Loader />
        ) : (
          <SpecialDropForm
            submitButtonCaption="Edit"
            initialValues={initialValues}
            isSubmitting={isProcessing}
            onSubmit={handleUpdateSpecialDrop}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
